import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { AIFeatureDetailPage } from './AIFeatureDetailPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// API helper
const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return await res.json();
    } catch (err) {
      console.error('API error:', err);
      return {};
    }
  },
  post: async (endpoint, data, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (err) {
      console.error('API error:', err);
      return { error: err.message };
    }
  }
};

function AIFeaturesPage({ token }) {
  const { language, t } = useLanguage();
  const [activeFeature, setActiveFeature] = useState(null);
  const [showDetailPage, setShowDetailPage] = useState(null); // For showing detail page
  const [formData, setFormData] = useState({});
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  
  // Voice Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = React.useRef(null);
  const audioChunksRef = React.useRef([]);
  
  // Voice Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Convert to base64
        const reader = new FileReader();
        reader.onload = async () => {
          const base64Audio = reader.result.split(',')[1];
          
          // Send to API for transcription
          setIsTranscribing(true);
          try {
            const res = await api.post('/api/ai/voice-to-text', { audio_base64: base64Audio }, token);
            if (res.success && res.text) {
              // Append transcribed text to question field
              setFormData(prev => ({
                ...prev,
                question: (prev.question || '') + (prev.question ? ' ' : '') + res.text
              }));
            }
          } catch (err) {
            console.error('Transcription error:', err);
          }
          setIsTranscribing(false);
        };
        reader.readAsDataURL(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Microphone error:', err);
      alert(language === 'hi' ? 'माइक्रोफ़ोन एक्सेस करने में समस्या। कृपया अनुमति दें।' : 'Error accessing microphone. Please allow permission.');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };
  
  // Doubt History State
  const [showDoubtHistory, setShowDoubtHistory] = useState(false);
  const [doubtHistory, setDoubtHistory] = useState([]);
  const [doubtAnalysis, setDoubtAnalysis] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Fetch Doubt History
  const fetchDoubtHistory = async () => {
    setLoadingHistory(true);
    try {
      const [historyRes, analysisRes] = await Promise.all([
        api.get('/api/doubt-history/recent?limit=10', token),
        api.get('/api/doubt-history/my-analysis', token)
      ]);
      if (historyRes.success) setDoubtHistory(historyRes.doubts || []);
      if (analysisRes.success) setDoubtAnalysis(analysisRes);
    } catch (err) {
      console.error('Error fetching doubt history:', err);
    }
    setLoadingHistory(false);
  };
  
  // Load history when showing
  React.useEffect(() => {
    if (showDoubtHistory) {
      fetchDoubtHistory();
    }
  }, [showDoubtHistory]);

  // All 31 AI Features - All LIVE and Working!
  const features = [
    // ============ ADMIN/TEACHER FEATURES ============
    { id: 'simplify-notice', name: language === 'hi' ? 'नोटिस सरलकर्ता' : 'Notice Simplifier', icon: '📝', desc: language === 'hi' ? 'लंबी notices को WhatsApp के लिए छोटा और सरल करें। Parents को समझने में आसान!' : 'Convert long notices into simple WhatsApp-friendly messages. Easy for parents!', category: 'admin', status: 'live' },
    { id: 'progress-summary', name: language === 'hi' ? 'प्रगति सारांश' : 'Progress Summary', icon: '📊', desc: language === 'hi' ? 'Student का पूरा progress report AI से generate करें - attendance, marks, behaviour सब एक साथ' : 'Generate complete student progress report with AI - attendance, marks, behaviour together', category: 'teacher', status: 'live' },
    { id: 'complaint-draft', name: language === 'hi' ? 'शिकायत उत्तर' : 'Complaint Reply', icon: '📨', desc: language === 'hi' ? 'Parent complaints का professional reply draft करें। Tone: empathetic, formal या urgent' : 'Draft professional replies to parent complaints. Tone: empathetic, formal or urgent', category: 'admin', status: 'live' },
    { id: 'fee-reminder', name: language === 'hi' ? 'शुल्क रिमाइंडर' : 'Fee Reminder', icon: '💰', desc: language === 'hi' ? 'Fee pending parents को respectful reminder messages generate करें' : 'Generate respectful fee reminder messages for pending payments', category: 'admin', status: 'live' },
    { id: 'compliance-checklist', name: language === 'hi' ? 'अनुपालन चेकलिस्ट' : 'Compliance Checklist', icon: '✅', desc: language === 'hi' ? 'UDISE+, Scholarship, Inspection के लिए step-by-step checklist generate करें' : 'Generate step-by-step checklist for UDISE+, Scholarship, Inspection', category: 'admin', status: 'live' },
    { id: 'complaint-guidance', name: language === 'hi' ? 'शिकायत मार्गदर्शन' : 'Complaint Guidance', icon: '🔍', desc: language === 'hi' ? 'किसी भी complaint को handle करने की professional guidance पाएं' : 'Get professional guidance on handling any type of complaint', category: 'admin', status: 'live' },
    { id: 'communication-template', name: language === 'hi' ? 'संदेश टेम्पलेट्स' : 'Message Templates', icon: '📱', desc: language === 'hi' ? 'Events, holidays, reminders के लिए WhatsApp broadcast templates' : 'WhatsApp broadcast templates for events, holidays, reminders', category: 'admin', status: 'live' },
    { id: 'student-support', name: language === 'hi' ? 'सहायता पहचान' : 'Support Identifier', icon: '🎯', desc: language === 'hi' ? 'किसी student की performance analyze करके support needs identify करें' : 'Analyze student performance and identify support needs', category: 'teacher', status: 'live' },
    { id: 'workflow-reminder', name: language === 'hi' ? 'ऑटो रिमाइंडर' : 'Auto Reminders', icon: '⏰', desc: language === 'hi' ? 'Events, deadlines के लिए WhatsApp, SMS, Email reminders generate करें' : 'Generate WhatsApp, SMS, Email reminders for events and deadlines', category: 'admin', status: 'live' },
    { id: 'content-improver', name: language === 'hi' ? 'सामग्री सुधारक' : 'Content Improver', icon: '✨', desc: language === 'hi' ? 'Homework, notices को और professional और clear बनाएं' : 'Make homework and notices more professional and clear', category: 'teacher', status: 'live' },
    
    // ============ STUDENT FEATURES ============
    { id: 'generate-mcq', name: language === 'hi' ? 'MCQ जनरेटर' : 'MCQ Generator', icon: '❓', desc: language === 'hi' ? 'किसी भी topic पर practice MCQs generate करें - Easy/Medium/Hard' : 'Generate practice MCQs on any topic - Easy/Medium/Hard levels', category: 'student', status: 'live' },
    { id: 'homework-hint', name: language === 'hi' ? 'होमवर्क हिंट्स' : 'Homework Hints', icon: '💡', desc: language === 'hi' ? 'चाचा चौधरी, बिल्लू जैसे characters से मज़ेदार hints! Photo upload + Voice input supported' : 'Fun hints from Chacha Chaudhary, Billu! Photo upload + Voice input supported', category: 'student', status: 'live', hasStoryMode: true, hasImageUpload: true, hasVoiceInput: true },
    { id: 'study-plan', name: language === 'hi' ? 'स्टडी प्लान' : 'Study Plan', icon: '📅', desc: language === 'hi' ? 'Exam preparation के लिए personalized daily study plan बनाएं' : 'Create personalized daily study plan for exam preparation', category: 'student', status: 'live' },
    { id: 'micro-task', name: language === 'hi' ? 'माइक्रो टास्क' : 'Micro Tasks', icon: '⚡', desc: language === 'hi' ? '10-15 minutes में complete होने वाले quick study tasks पाएं' : 'Get quick study tasks that can be completed in 10-15 minutes', category: 'student', status: 'live' },
    { id: 'chapter-explanation', name: language === 'hi' ? 'अध्याय व्याख्या' : 'Chapter Explainer', icon: '📖', desc: language === 'hi' ? 'किसी भी chapter को simple Hindi में explain करवाएं' : 'Get any chapter explained in simple language', category: 'student', status: 'live' },
    { id: 'homework-revision', name: language === 'hi' ? 'होमवर्क असिस्टेंट' : 'Homework Assistant', icon: '📚', desc: language === 'hi' ? 'Class का homework और revision में AI की help लें' : 'Get AI help with class homework and revision', category: 'student', status: 'live' },
    
    // ============ ALL USERS FEATURES ============
    { id: 'faq', name: language === 'hi' ? 'FAQ बॉट' : 'FAQ Bot', icon: '🤖', desc: language === 'hi' ? 'School से related कोई भी सवाल पूछें - AI instantly जवाब देगा' : 'Ask any school-related question - AI will answer instantly', category: 'all', status: 'live' },
    { id: 'life-skills', name: language === 'hi' ? 'जीवन कौशल' : 'Life Skills', icon: '🌟', desc: language === 'hi' ? 'अनुशासन, समय प्रबंधन, digital safety जैसे life skills सीखें' : 'Learn life skills like discipline, time management, digital safety', category: 'all', status: 'live' },
    { id: 'format-adapter', name: language === 'hi' ? 'फॉर्मेट एडॉप्टर' : 'Format Adapter', icon: '🔄', desc: language === 'hi' ? 'Content को detailed, bullet points, WhatsApp format में convert करें' : 'Convert content to detailed, bullet points, WhatsApp format', category: 'all', status: 'live' },
    { id: 'parent-portal-simplify', name: language === 'hi' ? 'पेरेंट सरलकर्ता' : 'Parent Simplifier', icon: '👨‍👩‍👧', desc: language === 'hi' ? 'School की information को parents के लिए आसान भाषा में बदलें' : 'Convert school information to easy language for parents', category: 'parent', status: 'live' },
    
    // ============ NEW ADVANCED FEATURES ============
    { id: 'class-weak-topics', name: language === 'hi' ? 'क्लास Weak Topics' : 'Class Weak Topics', icon: '📉', desc: language === 'hi' ? 'पूरी class के doubt history से common weak topics analyze करें' : 'Analyze common weak topics from entire class doubt history', category: 'teacher', status: 'live' },
    { id: 'question-paper-gen', name: language === 'hi' ? 'प्रश्न पत्र जनरेटर' : 'Question Paper Generator', icon: '📄', desc: language === 'hi' ? 'Balanced question paper बनाएं - MCQ, Short, Long questions mix' : 'Generate balanced question paper - MCQ, Short, Long questions mix', category: 'teacher', status: 'live' },
    { id: 'report-card-gen', name: language === 'hi' ? 'रिपोर्ट कार्ड AI' : 'AI Report Card', icon: '🎓', desc: language === 'hi' ? 'Student की marks, attendance से auto report card और remarks generate करें' : 'Auto-generate report card and remarks from student marks and attendance', category: 'admin', status: 'live' },
    { id: 'at-risk-alerts', name: language === 'hi' ? 'At-Risk अलर्ट्स' : 'At-Risk Alerts', icon: '⚠️', desc: language === 'hi' ? 'पिछड़ते students को early identify करें - attendance, marks analysis' : 'Early identify struggling students - attendance and marks analysis', category: 'teacher', status: 'live' },
    
    // ============ PARENT FEATURES ============
    { id: 'child-progress', name: language === 'hi' ? 'बच्चे की प्रगति' : 'Child Progress', icon: '📈', desc: language === 'hi' ? 'बच्चे की complete progress dashboard - academics, attendance, behaviour' : 'Complete child progress dashboard - academics, attendance, behaviour', category: 'parent', status: 'live' },
    { id: 'daily-summary', name: language === 'hi' ? 'दैनिक सारांश' : 'Daily Summary', icon: '📋', desc: language === 'hi' ? 'आज school में बच्चे ने क्या सीखा - daily AI summary' : 'What child learned at school today - daily AI summary', category: 'parent', status: 'live' },
    { id: 'attendance-alerts', name: language === 'hi' ? 'उपस्थिति अलर्ट' : 'Attendance Alerts', icon: '🔔', desc: language === 'hi' ? 'बच्चे की attendance में कोई issue हो तो तुरंत alert पाएं' : 'Get instant alert if there is any issue with child attendance', category: 'parent', status: 'live' },
    
    // ============ GAMIFIED LEARNING ============
    { id: 'practice-tests', name: language === 'hi' ? 'प्रैक्टिस टेस्ट' : 'Practice Tests', icon: '📝', desc: language === 'hi' ? 'Unlimited practice tests लें - instant results और explanation' : 'Take unlimited practice tests - instant results and explanation', category: 'student', status: 'live' },
    { id: 'gamified-learning', name: language === 'hi' ? 'गेमिफाइड लर्निंग' : 'Gamified Learning', icon: '🏆', desc: language === 'hi' ? 'पढ़ाई को game बनाएं! Badges कमाएं, Streaks बनाएं, Leaderboard में आएं' : 'Make learning fun! Earn badges, build streaks, climb leaderboard', category: 'student', status: 'live' },
    { id: 'revision-scheduler', name: language === 'hi' ? 'रिवीज़न शेड्यूलर' : 'Revision Scheduler', icon: '🗓️', desc: language === 'hi' ? 'Spaced repetition से smart revision schedule बनाएं' : 'Create smart revision schedule with spaced repetition', category: 'student', status: 'live' },
    { id: 'weekly-challenge', name: language === 'hi' ? 'साप्ताहिक चैलेंज' : 'Weekly Challenge', icon: '🎯', desc: language === 'hi' ? 'हर हफ्ते नया quiz challenge! Top scorers को recognition' : 'New quiz challenge every week! Recognition for top scorers', category: 'student', status: 'live', isNew: true },
  ];

  // Count live and pending features
  const liveCount = features.filter(f => f.status === 'live').length;
  const pendingCount = features.filter(f => f.status === 'pending').length;

  const categories = [
    { id: 'all', name: language === 'hi' ? 'सभी' : 'All', count: features.length },
    { id: 'admin', name: language === 'hi' ? 'एडमिन' : 'Admin', count: features.filter(f => f.category === 'admin').length },
    { id: 'teacher', name: language === 'hi' ? 'शिक्षक' : 'Teacher', count: features.filter(f => f.category === 'teacher').length },
    { id: 'student', name: language === 'hi' ? 'विद्यार्थी' : 'Student', count: features.filter(f => f.category === 'student').length },
    { id: 'parent', name: language === 'hi' ? 'अभिभावक' : 'Parent', count: features.filter(f => f.category === 'parent').length },
  ];

  const filteredFeatures = activeCategory === 'all' ? features : features.filter(f => f.category === activeCategory || f.category === 'all');

  const handleSubmit = async () => {
    setLoading(true);
    setResponse(null);
    try {
      // Map features to their correct endpoints
      let endpoint = activeFeature;
      let method = 'POST';
      let url = `/api/ai/${activeFeature}`;
      
      // Special endpoint mappings
      if (activeFeature === 'practice-tests') {
        url = '/api/ai/practice-tests/generate';
      } else if (activeFeature === 'weekly-challenge') {
        // Different endpoints based on action
        const action = formData.action || 'get_current';
        if (action === 'get_current') {
          method = 'GET';
          url = `/api/ai/weekly-challenge/current?class_level=${encodeURIComponent(formData.class_level || 'Class 8')}`;
        } else if (action === 'leaderboard') {
          method = 'GET';
          url = '/api/ai/weekly-challenge/leaderboard';
        } else if (action === 'history') {
          method = 'GET';
          url = '/api/ai/weekly-challenge/history';
        }
      }
      
      let res;
      if (method === 'GET') {
        res = await api.get(url, token);
      } else {
        res = await api.post(url, formData, token);
      }
      setResponse(res);
    } catch (err) {
      setResponse({ success: false, message: err.message });
    }
    setLoading(false);
  };

  // Comic characters for Story Mode
  const comicCharacters = [
    { id: 'chacha', name: 'चाचा चौधरी', emoji: '👴', trait: language === 'hi' ? 'बुद्धिमान' : 'Wise' },
    { id: 'sabu', name: 'साबू', emoji: '💪', trait: language === 'hi' ? 'शक्तिशाली' : 'Powerful' },
    { id: 'billu', name: 'बिल्लू', emoji: '👦', trait: language === 'hi' ? 'शरारती' : 'Mischievous' },
    { id: 'pinki', name: 'पिंकी', emoji: '👧', trait: language === 'hi' ? 'समझदार' : 'Smart' },
  ];

  const renderForm = () => {
    switch (activeFeature) {
      case 'simplify-notice':
      case 'parent-portal-simplify':
        return (
          <div className="space-y-4">
            <textarea placeholder={language === 'hi' ? "नोटिस/सर्कुलर टेक्स्ट यहां पेस्ट करें..." : "Paste notice/circular text here..."} value={formData.original_text || ''} onChange={(e) => setFormData({ ...formData, original_text: e.target.value })} className="w-full px-4 py-3 border rounded-lg h-32" />
            <select value={formData.format || 'whatsapp'} onChange={(e) => setFormData({ ...formData, format: e.target.value })} className="w-full px-4 py-3 border rounded-lg">
              <option value="whatsapp">WhatsApp Format</option>
              <option value="detailed">{language === 'hi' ? 'विस्तृत' : 'Detailed'}</option>
              <option value="bullet">{language === 'hi' ? 'बुलेट पॉइंट्स' : 'Bullet Points'}</option>
            </select>
          </div>
        );

      case 'generate-mcq':
        return (
          <div className="space-y-4">
            <input placeholder={language === 'hi' ? "विषय (जैसे: Science)" : "Subject (e.g., Science)"} value={formData.subject || ''} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="w-full px-4 py-3 border rounded-lg" />
            <input placeholder={language === 'hi' ? "टॉपिक (जैसे: प्रकाश संश्लेषण)" : "Topic (e.g., Photosynthesis)"} value={formData.topic || ''} onChange={(e) => setFormData({ ...formData, topic: e.target.value })} className="w-full px-4 py-3 border rounded-lg" />
            <input placeholder={language === 'hi' ? "कक्षा (जैसे: Class 8)" : "Class (e.g., Class 8)"} value={formData.class_level || ''} onChange={(e) => setFormData({ ...formData, class_level: e.target.value })} className="w-full px-4 py-3 border rounded-lg" />
            <div className="grid grid-cols-2 gap-4">
              <input type="number" placeholder={language === 'hi' ? "प्रश्न (5)" : "Questions (5)"} value={formData.count || 5} onChange={(e) => setFormData({ ...formData, count: parseInt(e.target.value) })} className="px-4 py-3 border rounded-lg" />
              <select value={formData.difficulty || 'medium'} onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })} className="px-4 py-3 border rounded-lg">
                <option value="easy">{language === 'hi' ? 'आसान' : 'Easy'}</option>
                <option value="medium">{language === 'hi' ? 'मध्यम' : 'Medium'}</option>
                <option value="hard">{language === 'hi' ? 'कठिन' : 'Hard'}</option>
              </select>
            </div>
          </div>
        );

      case 'faq':
        return <textarea placeholder={language === 'hi' ? "अपना सवाल पूछें..." : "Ask your question..."} value={formData.question || ''} onChange={(e) => setFormData({ ...formData, question: e.target.value })} className="w-full px-4 py-3 border rounded-lg h-24" />;

      case 'homework-hint':
        return (
          <div className="space-y-4">
            {/* Story Mode Toggle */}
            <div className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-orange-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🦸</span>
                  <span className="font-semibold text-orange-800">{language === 'hi' ? 'स्टोरी मोड' : 'Story Mode'}</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={formData.story_mode || false} onChange={(e) => setFormData({ ...formData, story_mode: e.target.checked })} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                </label>
              </div>
              {formData.story_mode && (
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {comicCharacters.map(char => (
                    <button key={char.id} type="button" onClick={() => setFormData({ ...formData, character: char.id })}
                      className={`p-2 rounded-lg text-center transition ${formData.character === char.id ? 'bg-orange-500 text-white' : 'bg-white hover:bg-orange-100'}`}>
                      <span className="text-2xl block">{char.emoji}</span>
                      <span className="text-xs font-medium">{char.name}</span>
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-orange-700 mt-2">
                {language === 'hi' 
                  ? 'चाचा चौधरी, बिल्लू जैसे 90s कॉमिक कैरेक्टर के साथ मज़ेदार कहानी में hints!'
                  : 'Fun story hints with 90s comic characters like Chacha Chaudhary!'}
              </p>
            </div>
            
            {/* Image Upload Section - NEW */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">📷</span>
                <span className="font-semibold text-blue-800">{language === 'hi' ? 'फोटो अपलोड करें' : 'Upload Photo'}</span>
                <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">{language === 'hi' ? 'नया!' : 'New!'}</span>
              </div>
              <p className="text-xs text-blue-700 mb-3">
                {language === 'hi' 
                  ? 'अपनी notebook या किताब की फोटो upload करें - AI उसे देखकर समझाएगा!'
                  : 'Upload a photo of your notebook or textbook - AI will explain it!'}
              </p>
              
              {/* Image Preview */}
              {formData.image_preview && (
                <div className="relative mb-3">
                  <img src={formData.image_preview} alt="Uploaded" className="max-h-48 rounded-lg border border-blue-300 mx-auto" />
                  <button 
                    type="button" 
                    onClick={() => setFormData({ ...formData, image_base64: null, image_preview: null })}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              )}
              
              {/* Upload Button */}
              {!formData.image_preview && (
                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-blue-300 border-dashed rounded-lg cursor-pointer bg-white hover:bg-blue-50 transition">
                  <div className="flex flex-col items-center justify-center">
                    <svg className="w-8 h-8 text-blue-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-blue-500">{language === 'hi' ? 'यहाँ क्लिक करें या फोटो खींचें' : 'Click here or drag photo'}</p>
                  </div>
                  <input 
                    type="file" 
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden" 
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Check file size (max 5MB)
                        if (file.size > 5 * 1024 * 1024) {
                          alert(language === 'hi' ? 'फाइल 5MB से छोटी होनी चाहिए' : 'File must be smaller than 5MB');
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const base64 = event.target.result;
                          // Extract just the base64 data (remove data:image/...;base64, prefix)
                          const base64Data = base64.split(',')[1];
                          setFormData({ 
                            ...formData, 
                            image_base64: base64Data,
                            image_preview: base64 
                          });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    data-testid="homework-image-upload"
                  />
                </label>
              )}
              
              <p className="text-xs text-gray-500 mt-2 text-center">
                {language === 'hi' ? 'JPEG, PNG या WebP (Max 5MB)' : 'JPEG, PNG or WebP (Max 5MB)'}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <input placeholder={language === 'hi' ? "विषय" : "Subject"} value={formData.subject || ''} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="px-4 py-3 border rounded-lg" data-testid="homework-subject" />
              <input placeholder={language === 'hi' ? "कक्षा" : "Class Level"} value={formData.class_level || ''} onChange={(e) => setFormData({ ...formData, class_level: e.target.value })} className="px-4 py-3 border rounded-lg" data-testid="homework-class" />
            </div>
            <input placeholder={language === 'hi' ? "टॉपिक" : "Topic"} value={formData.topic || ''} onChange={(e) => setFormData({ ...formData, topic: e.target.value })} className="w-full px-4 py-3 border rounded-lg" data-testid="homework-topic" />
            
            {/* Question with Voice Input */}
            <div className="relative">
              <textarea 
                placeholder={language === 'hi' ? "सवाल/समस्या जिसमें help चाहिए... (बोलकर भी पूछ सकते हैं!)" : "Question/problem you need help with... (you can also speak!)"} 
                value={formData.question || ''} 
                onChange={(e) => setFormData({ ...formData, question: e.target.value })} 
                className="w-full px-4 py-3 pr-16 border rounded-lg h-24" 
                data-testid="homework-question" 
              />
              
              {/* Voice Input Button */}
              <div className="absolute right-2 bottom-2 flex items-center gap-1">
                {isTranscribing ? (
                  <div className="flex items-center gap-2 text-orange-600 text-sm">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>{language === 'hi' ? 'सुन रहा...' : 'Listening...'}</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`p-2 rounded-full transition-all ${
                      isRecording 
                        ? 'bg-red-500 text-white animate-pulse shadow-lg' 
                        : 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                    }`}
                    title={language === 'hi' ? (isRecording ? 'रुकें' : 'बोलकर पूछें') : (isRecording ? 'Stop' : 'Speak your question')}
                    data-testid="voice-input-btn"
                  >
                    {isRecording ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <rect x="6" y="6" width="12" height="12" rx="2" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </div>
            
            {/* Voice Input Hint */}
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <svg className="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
              </svg>
              {language === 'hi' 
                ? 'माइक बटन दबाकर Hindi में बोलें - AI सुनकर लिख देगा!'
                : 'Press mic button and speak in Hindi - AI will transcribe!'}
            </p>
          </div>
        );

      case 'fee-reminder':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input placeholder="Parent Name" value={formData.parent_name || ''} onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })} className="px-4 py-3 border rounded-lg" />
              <input placeholder="Student Name" value={formData.student_name || ''} onChange={(e) => setFormData({ ...formData, student_name: e.target.value })} className="px-4 py-3 border rounded-lg" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <input placeholder="Class" value={formData.class_name || ''} onChange={(e) => setFormData({ ...formData, class_name: e.target.value })} className="px-4 py-3 border rounded-lg" />
              <input type="number" placeholder="Due Amount ₹" value={formData.due_amount || ''} onChange={(e) => setFormData({ ...formData, due_amount: parseFloat(e.target.value) })} className="px-4 py-3 border rounded-lg" />
              <input placeholder="Due Date" value={formData.due_date || ''} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} className="px-4 py-3 border rounded-lg" />
            </div>
            <select value={formData.tone || 'respectful'} onChange={(e) => setFormData({ ...formData, tone: e.target.value })} className="w-full px-4 py-3 border rounded-lg">
              <option value="respectful">Respectful (सम्मानजनक)</option>
              <option value="urgent">Urgent (जरूरी)</option>
              <option value="final">Final Reminder (अंतिम)</option>
            </select>
          </div>
        );

      case 'study-plan':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input placeholder="Student Name" value={formData.student_name || ''} onChange={(e) => setFormData({ ...formData, student_name: e.target.value })} className="px-4 py-3 border rounded-lg" />
              <input placeholder="Class Level" value={formData.class_level || ''} onChange={(e) => setFormData({ ...formData, class_level: e.target.value })} className="px-4 py-3 border rounded-lg" />
            </div>
            <input placeholder="Subjects (comma separated): Hindi, Math, Science" value={formData.subjects?.join(', ') || ''} onChange={(e) => setFormData({ ...formData, subjects: e.target.value.split(',').map(s => s.trim()) })} className="w-full px-4 py-3 border rounded-lg" />
            <input type="number" placeholder="Duration (minutes): 30" value={formData.duration_minutes || 30} onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })} className="w-full px-4 py-3 border rounded-lg" />
          </div>
        );

      case 'complaint-draft':
        return (
          <div className="space-y-4">
            <input placeholder="Parent Name" value={formData.parent_name || ''} onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })} className="w-full px-4 py-3 border rounded-lg" />
            <select value={formData.complaint_category || 'academic'} onChange={(e) => setFormData({ ...formData, complaint_category: e.target.value })} className="w-full px-4 py-3 border rounded-lg">
              <option value="academic">Academic (शैक्षणिक)</option>
              <option value="fee">Fee (शुल्क)</option>
              <option value="behaviour">Behaviour (व्यवहार)</option>
              <option value="safety">Safety (सुरक्षा)</option>
              <option value="facilities">Facilities (सुविधाएं)</option>
            </select>
            <textarea placeholder="Complaint Text..." value={formData.complaint_text || ''} onChange={(e) => setFormData({ ...formData, complaint_text: e.target.value })} className="w-full px-4 py-3 border rounded-lg h-24" />
          </div>
        );

      case 'progress-summary':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input placeholder="Student Name" value={formData.student_name || ''} onChange={(e) => setFormData({ ...formData, student_name: e.target.value })} className="px-4 py-3 border rounded-lg" />
              <input placeholder="Class" value={formData.class_name || ''} onChange={(e) => setFormData({ ...formData, class_name: e.target.value })} className="px-4 py-3 border rounded-lg" />
            </div>
            <input placeholder="Subjects (comma separated)" value={formData.subjects?.join(', ') || ''} onChange={(e) => setFormData({ ...formData, subjects: e.target.value.split(',').map(s => s.trim()) })} className="w-full px-4 py-3 border rounded-lg" />
            <input type="number" placeholder="Attendance %" value={formData.attendance_percentage || ''} onChange={(e) => setFormData({ ...formData, attendance_percentage: parseFloat(e.target.value) })} className="w-full px-4 py-3 border rounded-lg" />
            <textarea placeholder="Marks Summary (optional)" value={formData.marks_summary || ''} onChange={(e) => setFormData({ ...formData, marks_summary: e.target.value })} className="w-full px-4 py-3 border rounded-lg h-20" />
          </div>
        );

      case 'life-skills':
        return (
          <div className="space-y-4">
            <select value={formData.topic || 'discipline'} onChange={(e) => setFormData({ ...formData, topic: e.target.value })} className="w-full px-4 py-3 border rounded-lg">
              <option value="discipline">अनुशासन (Discipline)</option>
              <option value="routine">दैनिक दिनचर्या (Routine)</option>
              <option value="digital_safety">इंटरनेट सुरक्षा (Digital Safety)</option>
              <option value="manners">शिष्टाचार (Manners)</option>
              <option value="health">स्वास्थ्य (Health)</option>
            </select>
            <select value={formData.target_audience || 'student'} onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })} className="w-full px-4 py-3 border rounded-lg">
              <option value="student">Student के लिए</option>
              <option value="parent">Parent के लिए</option>
            </select>
          </div>
        );

      case 'compliance-checklist':
        return (
          <select value={formData.task_type || 'udise'} onChange={(e) => setFormData({ ...formData, task_type: e.target.value })} className="w-full px-4 py-3 border rounded-lg">
            <option value="udise">UDISE+ Portal Entry</option>
            <option value="scholarship">Scholarship Application</option>
            <option value="inspection">School Inspection</option>
            <option value="annual_report">Annual Report</option>
          </select>
        );

      case 'chapter-explanation':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input placeholder="Subject" value={formData.subject || ''} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="px-4 py-3 border rounded-lg" />
              <input placeholder="Class Level" value={formData.class_level || ''} onChange={(e) => setFormData({ ...formData, class_level: e.target.value })} className="px-4 py-3 border rounded-lg" />
            </div>
            <input placeholder="Chapter Name" value={formData.chapter_name || ''} onChange={(e) => setFormData({ ...formData, chapter_name: e.target.value })} className="w-full px-4 py-3 border rounded-lg" />
            <textarea placeholder="Specific doubt (optional)" value={formData.specific_doubt || ''} onChange={(e) => setFormData({ ...formData, specific_doubt: e.target.value })} className="w-full px-4 py-3 border rounded-lg h-20" />
          </div>
        );

      case 'micro-task':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input placeholder="Class Level" value={formData.class_level || ''} onChange={(e) => setFormData({ ...formData, class_level: e.target.value })} className="px-4 py-3 border rounded-lg" />
              <input type="number" placeholder="Minutes Available: 10" value={formData.time_available_minutes || 10} onChange={(e) => setFormData({ ...formData, time_available_minutes: parseInt(e.target.value) })} className="px-4 py-3 border rounded-lg" />
            </div>
            <input placeholder="Subject (optional)" value={formData.subject || ''} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="w-full px-4 py-3 border rounded-lg" />
          </div>
        );

      case 'communication-template':
        return (
          <div className="space-y-4">
            <select value={formData.message_purpose || 'announcement'} onChange={(e) => setFormData({ ...formData, message_purpose: e.target.value })} className="w-full px-4 py-3 border rounded-lg">
              <option value="announcement">Announcement (घोषणा)</option>
              <option value="reminder">Reminder (रिमाइंडर)</option>
              <option value="event">Event (कार्यक्रम)</option>
              <option value="holiday">Holiday (छुट्टी)</option>
            </select>
            <textarea placeholder="Key Points (one per line)" value={formData.key_points?.join('\n') || ''} onChange={(e) => setFormData({ ...formData, key_points: e.target.value.split('\n').filter(p => p.trim()) })} className="w-full px-4 py-3 border rounded-lg h-24" />
          </div>
        );

      case 'format-adapter':
        return (
          <div className="space-y-4">
            <textarea placeholder="Original content..." value={formData.original_text || ''} onChange={(e) => setFormData({ ...formData, original_text: e.target.value })} className="w-full px-4 py-3 border rounded-lg h-32" />
            <select value={formData.target_format || 'short_whatsapp'} onChange={(e) => setFormData({ ...formData, target_format: e.target.value })} className="w-full px-4 py-3 border rounded-lg">
              <option value="detailed">Detailed (विस्तृत)</option>
              <option value="short_whatsapp">Short WhatsApp</option>
              <option value="bullet">Bullet Points</option>
              <option value="formal">Formal (औपचारिक)</option>
            </select>
          </div>
        );

      case 'homework-revision':
        return (
          <div className="space-y-4">
            <input placeholder="Class (जैसे: Class 5)" value={formData.class_name || ''} onChange={(e) => setFormData({ ...formData, class_name: e.target.value })} className="w-full px-4 py-3 border rounded-lg" />
            <input placeholder="Subject (optional)" value={formData.subject || ''} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="w-full px-4 py-3 border rounded-lg" />
          </div>
        );

      case 'content-improver':
        return (
          <div className="space-y-4">
            <textarea placeholder="Original content..." value={formData.original_text || ''} onChange={(e) => setFormData({ ...formData, original_text: e.target.value })} className="w-full px-4 py-3 border rounded-lg h-32" />
            <select value={formData.content_type || 'notice'} onChange={(e) => setFormData({ ...formData, content_type: e.target.value })} className="w-full px-4 py-3 border rounded-lg">
              <option value="homework">Homework</option>
              <option value="notice">Notice</option>
              <option value="message">Message</option>
            </select>
          </div>
        );

      case 'complaint-guidance':
        return (
          <div className="space-y-4">
            <select value={formData.complaint_category || 'academic'} onChange={(e) => setFormData({ ...formData, complaint_category: e.target.value })} className="w-full px-4 py-3 border rounded-lg">
              <option value="academic">Academic</option>
              <option value="fee">Fee</option>
              <option value="behaviour">Behaviour</option>
              <option value="safety">Safety</option>
            </select>
            <textarea placeholder="Complaint text..." value={formData.complaint_text || ''} onChange={(e) => setFormData({ ...formData, complaint_text: e.target.value })} className="w-full px-4 py-3 border rounded-lg h-24" />
          </div>
        );

      // ============== NEW AI FEATURES FORMS ==============
      
      case 'class-weak-topics':
        return (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              {language === 'hi' 
                ? '📊 यह feature पूरी class के doubt history को analyze करके common weak topics दिखाता है।'
                : '📊 This feature analyzes the entire class doubt history to show common weak topics.'}
            </div>
            <input placeholder={language === 'hi' ? "कक्षा (जैसे: Class 8)" : "Class (e.g., Class 8)"} value={formData.class_name || ''} onChange={(e) => setFormData({ ...formData, class_name: e.target.value })} className="w-full px-4 py-3 border rounded-lg" data-testid="class-weak-topics-class" />
            <input placeholder={language === 'hi' ? "विषय (optional)" : "Subject (optional)"} value={formData.subject || ''} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="w-full px-4 py-3 border rounded-lg" />
          </div>
        );

      case 'question-paper-gen':
        return (
          <div className="space-y-4">
            <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-700">
              {language === 'hi' 
                ? '📄 CBSE pattern में balanced question paper generate करें - MCQ, Short Answer, Long Answer के साथ।'
                : '📄 Generate balanced question paper in CBSE pattern - with MCQ, Short & Long Answers.'}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input placeholder={language === 'hi' ? "विषय" : "Subject"} value={formData.subject || ''} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="px-4 py-3 border rounded-lg" data-testid="qp-subject" />
              <input placeholder={language === 'hi' ? "कक्षा" : "Class"} value={formData.class_level || ''} onChange={(e) => setFormData({ ...formData, class_level: e.target.value })} className="px-4 py-3 border rounded-lg" data-testid="qp-class" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input type="number" placeholder={language === 'hi' ? "कुल अंक (100)" : "Total Marks (100)"} value={formData.total_marks || 100} onChange={(e) => setFormData({ ...formData, total_marks: parseInt(e.target.value) })} className="px-4 py-3 border rounded-lg" />
              <input type="number" placeholder={language === 'hi' ? "समय (मिनट)" : "Duration (min)"} value={formData.duration_minutes || 180} onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })} className="px-4 py-3 border rounded-lg" />
            </div>
            <textarea placeholder={language === 'hi' ? "Topics (एक per line)" : "Topics (one per line)"} value={formData.topics?.join('\n') || ''} onChange={(e) => setFormData({ ...formData, topics: e.target.value.split('\n').filter(t => t.trim()) })} className="w-full px-4 py-3 border rounded-lg h-20" />
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="p-2 bg-green-50 rounded text-center">Easy: 30%</div>
              <div className="p-2 bg-yellow-50 rounded text-center">Medium: 50%</div>
              <div className="p-2 bg-red-50 rounded text-center">Hard: 20%</div>
            </div>
          </div>
        );

      case 'report-card-gen':
        return (
          <div className="space-y-4">
            <div className="p-3 bg-green-50 rounded-lg text-sm text-green-700">
              {language === 'hi' 
                ? '🎓 Student का AI-powered report card generate करें - Strengths, Improvements, Teacher Remarks के साथ।'
                : '🎓 Generate AI-powered report card - with Strengths, Improvements, Teacher Remarks.'}
            </div>
            <input placeholder={language === 'hi' ? "Student ID" : "Student ID"} value={formData.student_id || ''} onChange={(e) => setFormData({ ...formData, student_id: e.target.value })} className="w-full px-4 py-3 border rounded-lg" data-testid="report-student-id" />
            <select value={formData.term || 'annual'} onChange={(e) => setFormData({ ...formData, term: e.target.value })} className="w-full px-4 py-3 border rounded-lg">
              <option value="quarterly">{language === 'hi' ? 'त्रैमासिक' : 'Quarterly'}</option>
              <option value="half_yearly">{language === 'hi' ? 'अर्धवार्षिक' : 'Half Yearly'}</option>
              <option value="annual">{language === 'hi' ? 'वार्षिक' : 'Annual'}</option>
            </select>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={formData.include_remarks !== false} onChange={(e) => setFormData({ ...formData, include_remarks: e.target.checked })} className="rounded" />
                {language === 'hi' ? 'Teacher Remarks शामिल करें' : 'Include Teacher Remarks'}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={formData.include_attendance !== false} onChange={(e) => setFormData({ ...formData, include_attendance: e.target.checked })} className="rounded" />
                {language === 'hi' ? 'Attendance शामिल करें' : 'Include Attendance'}
              </label>
            </div>
          </div>
        );

      case 'at-risk-alerts':
        return (
          <div className="space-y-4">
            <div className="p-3 bg-red-50 rounded-lg text-sm text-red-700">
              {language === 'hi' 
                ? '⚠️ पिछड़ते students की पहचान करें - Low Attendance, Low Marks के based पर।'
                : '⚠️ Identify struggling students - based on Low Attendance, Low Marks.'}
            </div>
            <input placeholder={language === 'hi' ? "Class ID (optional)" : "Class ID (optional)"} value={formData.class_id || ''} onChange={(e) => setFormData({ ...formData, class_id: e.target.value })} className="w-full px-4 py-3 border rounded-lg" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">{language === 'hi' ? 'Attendance Threshold %' : 'Attendance Threshold %'}</label>
                <input type="number" value={formData.threshold_attendance || 75} onChange={(e) => setFormData({ ...formData, threshold_attendance: parseFloat(e.target.value) })} className="w-full px-4 py-3 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">{language === 'hi' ? 'Marks Threshold %' : 'Marks Threshold %'}</label>
                <input type="number" value={formData.threshold_marks || 40} onChange={(e) => setFormData({ ...formData, threshold_marks: parseFloat(e.target.value) })} className="w-full px-4 py-3 border rounded-lg" />
              </div>
            </div>
          </div>
        );

      case 'child-progress':
        return (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              {language === 'hi' 
                ? '📈 अपने बच्चे की detailed progress देखें - Attendance, Marks, Learning Activity के साथ।'
                : '📈 View your child\'s detailed progress - with Attendance, Marks, Learning Activity.'}
            </div>
            <input placeholder={language === 'hi' ? "Student ID" : "Student ID"} value={formData.student_id || ''} onChange={(e) => setFormData({ ...formData, student_id: e.target.value })} className="w-full px-4 py-3 border rounded-lg" data-testid="child-progress-id" />
            <select value={formData.period || 'monthly'} onChange={(e) => setFormData({ ...formData, period: e.target.value })} className="w-full px-4 py-3 border rounded-lg">
              <option value="weekly">{language === 'hi' ? 'साप्ताहिक' : 'Weekly'}</option>
              <option value="monthly">{language === 'hi' ? 'मासिक' : 'Monthly'}</option>
              <option value="quarterly">{language === 'hi' ? 'त्रैमासिक' : 'Quarterly'}</option>
            </select>
          </div>
        );

      case 'daily-summary':
        return (
          <div className="space-y-4">
            <div className="p-3 bg-orange-50 rounded-lg text-sm text-orange-700">
              {language === 'hi' 
                ? '📋 आज आपके बच्चे ने क्या सीखा? Daily summary WhatsApp format में।'
                : '📋 What did your child learn today? Daily summary in WhatsApp format.'}
            </div>
            <input placeholder={language === 'hi' ? "Student ID" : "Student ID"} value={formData.student_id || ''} onChange={(e) => setFormData({ ...formData, student_id: e.target.value })} className="w-full px-4 py-3 border rounded-lg" data-testid="daily-summary-id" />
            <input type="date" value={formData.date || new Date().toISOString().split('T')[0]} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="w-full px-4 py-3 border rounded-lg" />
            
            {/* Teacher Topic Input - Issue 4 Fix */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <label className="block text-sm font-medium text-blue-700 mb-2">
                {language === 'hi' ? '👨‍🏫 शिक्षक: आज क्या पढ़ाया?' : '👨‍🏫 Teacher: What was taught today?'}
              </label>
              <textarea 
                placeholder={language === 'hi' ? 'आज की topics / activities दर्ज करें (जैसे: गणित में भिन्न, हिंदी में कविता, खेल कूद)' : 'Enter today\'s topics/activities (e.g., Fractions in Math, Hindi poem, Sports)'}
                value={formData.daily_topics || ''} 
                onChange={(e) => setFormData({ ...formData, daily_topics: e.target.value })} 
                className="w-full px-4 py-3 border rounded-lg h-20 bg-white" 
                data-testid="daily-topics-input"
              />
              <p className="text-xs text-blue-500 mt-1">{language === 'hi' ? '* यह जानकारी parents को भेजे जाने वाले summary में शामिल होगी' : '* This info will be included in the summary sent to parents'}</p>
            </div>
          </div>
        );

      case 'attendance-alerts':
        return (
          <div className="space-y-4">
            <div className="p-3 bg-yellow-50 rounded-lg text-sm text-yellow-700">
              {language === 'hi' 
                ? '🔔 बच्चे की attendance alerts और patterns देखें।'
                : '🔔 View your child\'s attendance alerts and patterns.'}
            </div>
            <input placeholder={language === 'hi' ? "Student ID" : "Student ID"} value={formData.student_id || ''} onChange={(e) => setFormData({ ...formData, student_id: e.target.value })} className="w-full px-4 py-3 border rounded-lg" data-testid="attendance-alert-id" />
            <div>
              <label className="block text-sm text-gray-600 mb-1">{language === 'hi' ? 'लगातार अनुपस्थिति Alert (दिन)' : 'Consecutive Absence Alert (days)'}</label>
              <input type="number" value={formData.alert_threshold || 3} onChange={(e) => setFormData({ ...formData, alert_threshold: parseInt(e.target.value) })} className="w-full px-4 py-3 border rounded-lg" />
            </div>
          </div>
        );

      case 'practice-tests':
        return (
          <div className="space-y-4">
            <div className="p-3 bg-indigo-50 rounded-lg text-sm text-indigo-700">
              {language === 'hi' 
                ? '📝 Unlimited practice tests लो! Subject और topic चुनो, AI instant test generate करेगा।'
                : '📝 Take unlimited practice tests! Choose subject & topic, AI generates instant test.'}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input placeholder={language === 'hi' ? "विषय" : "Subject"} value={formData.subject || ''} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="px-4 py-3 border rounded-lg" data-testid="practice-subject" />
              <input placeholder={language === 'hi' ? "कक्षा" : "Class"} value={formData.class_level || ''} onChange={(e) => setFormData({ ...formData, class_level: e.target.value })} className="px-4 py-3 border rounded-lg" />
            </div>
            <input placeholder={language === 'hi' ? "Topic" : "Topic"} value={formData.topic || ''} onChange={(e) => setFormData({ ...formData, topic: e.target.value })} className="w-full px-4 py-3 border rounded-lg" />
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">{language === 'hi' ? 'प्रश्न' : 'Questions'}</label>
                <input type="number" value={formData.num_questions || 10} onChange={(e) => setFormData({ ...formData, num_questions: parseInt(e.target.value) })} className="w-full px-4 py-3 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">{language === 'hi' ? 'समय (मिनट)' : 'Time (min)'}</label>
                <input type="number" value={formData.time_limit_minutes || 15} onChange={(e) => setFormData({ ...formData, time_limit_minutes: parseInt(e.target.value) })} className="w-full px-4 py-3 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">{language === 'hi' ? 'कठिनाई' : 'Difficulty'}</label>
                <select value={formData.difficulty || 'medium'} onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })} className="w-full px-4 py-3 border rounded-lg">
                  <option value="easy">{language === 'hi' ? 'आसान' : 'Easy'}</option>
                  <option value="medium">{language === 'hi' ? 'मध्यम' : 'Medium'}</option>
                  <option value="hard">{language === 'hi' ? 'कठिन' : 'Hard'}</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 'gamified-learning':
        return (
          <div className="space-y-4">
            <div className="p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg text-sm text-orange-700">
              {language === 'hi' 
                ? '🏆 Badges कमाओ, Streaks बनाओ, Leaderboard में top पर आओ!'
                : '🏆 Earn Badges, Build Streaks, Top the Leaderboard!'}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <button 
                type="button"
                onClick={() => setFormData({ ...formData, action: 'check_badges' })}
                className={`p-4 rounded-lg text-center transition ${formData.action === 'check_badges' ? 'bg-yellow-100 border-2 border-yellow-500' : 'bg-white border hover:border-yellow-300'}`}
              >
                <span className="text-3xl block mb-1">🏅</span>
                <span className="text-sm font-medium">{language === 'hi' ? 'मेरे Badges' : 'My Badges'}</span>
              </button>
              <button 
                type="button"
                onClick={() => setFormData({ ...formData, action: 'get_streak' })}
                className={`p-4 rounded-lg text-center transition ${formData.action === 'get_streak' ? 'bg-orange-100 border-2 border-orange-500' : 'bg-white border hover:border-orange-300'}`}
              >
                <span className="text-3xl block mb-1">🔥</span>
                <span className="text-sm font-medium">{language === 'hi' ? 'मेरी Streak' : 'My Streak'}</span>
              </button>
              <button 
                type="button"
                onClick={() => setFormData({ ...formData, action: 'get_leaderboard' })}
                className={`p-4 rounded-lg text-center transition ${formData.action === 'get_leaderboard' ? 'bg-purple-100 border-2 border-purple-500' : 'bg-white border hover:border-purple-300'}`}
              >
                <span className="text-3xl block mb-1">🏆</span>
                <span className="text-sm font-medium">{language === 'hi' ? 'Leaderboard' : 'Leaderboard'}</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 text-center">
              {language === 'hi' ? 'एक option चुनें और Generate करें' : 'Select an option and click Generate'}
            </p>
          </div>
        );

      case 'revision-scheduler':
        return (
          <div className="space-y-4">
            <div className="p-3 bg-teal-50 rounded-lg text-sm text-teal-700">
              {language === 'hi' 
                ? '🗓️ Exam से पहले smart revision schedule बनाओ - Spaced repetition के साथ!'
                : '🗓️ Create smart revision schedule before exam - with Spaced repetition!'}
            </div>
            <input placeholder={language === 'hi' ? "विषय" : "Subject"} value={formData.subject || ''} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="w-full px-4 py-3 border rounded-lg" data-testid="revision-subject" />
            <textarea placeholder={language === 'hi' ? "Topics (एक per line)" : "Topics (one per line)"} value={formData.topics?.join('\n') || ''} onChange={(e) => setFormData({ ...formData, topics: e.target.value.split('\n').filter(t => t.trim()) })} className="w-full px-4 py-3 border rounded-lg h-24" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">{language === 'hi' ? 'Exam Date' : 'Exam Date'}</label>
                <input type="date" value={formData.exam_date || ''} onChange={(e) => setFormData({ ...formData, exam_date: e.target.value })} className="w-full px-4 py-3 border rounded-lg" data-testid="revision-exam-date" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">{language === 'hi' ? 'Daily पढ़ाई (मिनट)' : 'Daily Study (min)'}</label>
                <input type="number" value={formData.daily_time_minutes || 30} onChange={(e) => setFormData({ ...formData, daily_time_minutes: parseInt(e.target.value) })} className="w-full px-4 py-3 border rounded-lg" />
              </div>
            </div>
          </div>
        );

      case 'weekly-challenge':
        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">🎯</span>
                <div>
                  <h3 className="font-bold text-purple-800">{language === 'hi' ? 'साप्ताहिक चैलेंज!' : 'Weekly Challenge!'}</h3>
                  <p className="text-sm text-purple-600">{language === 'hi' ? 'हर हफ्ते नया quiz - जीतो badges और points!' : 'New quiz every week - win badges and points!'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-purple-500">
                <span>🏆</span>
                <span>{language === 'hi' ? 'Leaderboard में top पर आओ!' : 'Top the Leaderboard!'}</span>
              </div>
            </div>
            
            {/* Class/Section Selection - Required before generating */}
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm font-medium text-yellow-800 mb-2">{language === 'hi' ? '⚠️ पहले कक्षा और सेक्शन चुनें' : '⚠️ First select class and section'}</p>
              <div className="grid grid-cols-2 gap-3">
                <select 
                  value={formData.class_level || ''} 
                  onChange={(e) => setFormData({ ...formData, class_level: e.target.value, section: '' })} 
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="weekly-challenge-class"
                >
                  <option value="">{language === 'hi' ? 'कक्षा चुनें *' : 'Select Class *'}</option>
                  {['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12'].map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <select 
                  value={formData.section || ''} 
                  onChange={(e) => setFormData({ ...formData, section: e.target.value })} 
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="weekly-challenge-section"
                >
                  <option value="">{language === 'hi' ? 'सेक्शन चुनें' : 'Select Section'}</option>
                  {['A', 'B', 'C', 'D'].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Action Buttons - Disabled until class selected */}
            <div className="grid grid-cols-3 gap-3">
              <button 
                type="button"
                disabled={!formData.class_level}
                onClick={() => setFormData({ ...formData, action: 'get_current' })}
                className={`p-4 rounded-lg text-center transition ${!formData.class_level ? 'opacity-50 cursor-not-allowed bg-gray-100' : formData.action === 'get_current' ? 'bg-purple-100 border-2 border-purple-500' : 'bg-white border hover:border-purple-300'}`}
              >
                <span className="text-2xl block mb-1">🎯</span>
                <span className="text-xs font-medium">{language === 'hi' ? 'इस हफ्ते का' : 'This Week'}</span>
              </button>
              <button 
                type="button"
                disabled={!formData.class_level}
                onClick={() => setFormData({ ...formData, action: 'leaderboard' })}
                className={`p-4 rounded-lg text-center transition ${!formData.class_level ? 'opacity-50 cursor-not-allowed bg-gray-100' : formData.action === 'leaderboard' ? 'bg-yellow-100 border-2 border-yellow-500' : 'bg-white border hover:border-yellow-300'}`}
              >
                <span className="text-2xl block mb-1">🏆</span>
                <span className="text-xs font-medium">{language === 'hi' ? 'Leaderboard' : 'Leaderboard'}</span>
              </button>
              <button 
                type="button"
                disabled={!formData.class_level}
                onClick={() => setFormData({ ...formData, action: 'history' })}
                className={`p-4 rounded-lg text-center transition ${!formData.class_level ? 'opacity-50 cursor-not-allowed bg-gray-100' : formData.action === 'history' ? 'bg-blue-100 border-2 border-blue-500' : 'bg-white border hover:border-blue-300'}`}
              >
                <span className="text-2xl block mb-1">📊</span>
                <span className="text-xs font-medium">{language === 'hi' ? 'मेरा Record' : 'My History'}</span>
              </button>
            </div>
            
            {formData.class_level && (
              <p className="text-xs text-green-600 text-center">
                ✓ {language === 'hi' ? `${formData.class_level}${formData.section ? ' - ' + formData.section : ''} के लिए challenge` : `Challenge for ${formData.class_level}${formData.section ? ' - ' + formData.section : ''}`}
              </p>
            )}
            
            <p className="text-xs text-gray-500 text-center">
              {language === 'hi' ? 'कक्षा चुनें, फिर Action चुनें और Generate करें।' : 'Select class, then choose action and click Generate.'}
            </p>
          </div>
        );

      default:
        return <p className="text-gray-500">{language === 'hi' ? 'इस feature के लिए form आ रहा है...' : 'Form for this feature is loading...'}</p>;
    }
  };

  const getResponseKey = () => {
    const keyMap = {
      'simplify-notice': 'simplified_text',
      'parent-portal-simplify': 'simplified_versions',
      'generate-mcq': 'mcqs',
      'faq': 'answer',
      'homework-hint': 'hints',
      'fee-reminder': 'reminder_message',
      'study-plan': 'study_plan',
      'complaint-draft': 'draft_reply',
      'progress-summary': 'summary',
      'life-skills': 'content',
      'compliance-checklist': 'checklist',
      'chapter-explanation': 'explanation',
      'micro-task': 'micro_tasks',
      'communication-template': 'templates',
      'format-adapter': 'adapted_content',
      'homework-revision': 'content',
      'content-improver': 'improved_content',
      'complaint-guidance': 'guidance',
      // New AI Features Response Keys
      'class-weak-topics': 'ai_insights',
      'question-paper-gen': 'question_paper',
      'report-card-gen': 'report_card',
      'at-risk-alerts': 'ai_recommendations',
      'child-progress': 'ai_summary',
      'daily-summary': 'daily_summary',
      'attendance-alerts': 'ai_advice',
      'practice-tests': 'questions',
      'gamified-learning': 'all_badges',
      'revision-scheduler': 'schedule',
      'weekly-challenge': 'challenge',
    };
    return keyMap[activeFeature] || 'data';
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{language === 'hi' ? 'AI सुविधाएं' : 'AI Features'} ({features.length})</h2>
          <div className="flex items-center gap-3 mt-2">
            <span className="flex items-center gap-1 text-sm">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <span className="text-green-700 font-medium">{liveCount} {language === 'hi' ? 'Live और Working' : 'Live & Working'}</span>
            </span>
            <span className="text-xs text-gray-400">Powered by GPT-5.2</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setShowDoubtHistory(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg hover:from-purple-600 hover:to-indigo-600 transition text-sm font-medium shadow"
            data-testid="doubt-history-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {language === 'hi' ? 'पिछले सवाल' : 'My Doubts'}
          </button>
          <span className="text-sm text-gray-500">Powered by GPT-5.2</span>
        </div>
      </div>

      {/* Doubt History Modal */}
      {showDoubtHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white p-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-bold">{language === 'hi' ? 'मेरे पिछले सवाल' : 'My Doubt History'}</h3>
                </div>
                <button onClick={() => setShowDoubtHistory(false)} className="p-1 hover:bg-white/20 rounded">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <svg className="animate-spin h-8 w-8 text-purple-500" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              ) : (
                <>
                  {/* Analysis Section */}
                  {doubtAnalysis && (
                    <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
                      <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                        <span className="text-xl">📊</span>
                        {language === 'hi' ? 'मेरी Analysis' : 'My Analysis'}
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <div className="bg-white p-3 rounded-lg text-center">
                          <div className="text-2xl font-bold text-purple-600">{doubtAnalysis.total_doubts || 0}</div>
                          <div className="text-xs text-gray-500">{language === 'hi' ? 'कुल सवाल' : 'Total Doubts'}</div>
                        </div>
                        <div className="bg-white p-3 rounded-lg text-center">
                          <div className="text-2xl font-bold text-indigo-600">{doubtAnalysis.subjects_asked?.length || 0}</div>
                          <div className="text-xs text-gray-500">{language === 'hi' ? 'विषय' : 'Subjects'}</div>
                        </div>
                        <div className="bg-white p-3 rounded-lg text-center">
                          <div className="text-2xl font-bold text-blue-600">{doubtAnalysis.weak_topics?.length || 0}</div>
                          <div className="text-xs text-gray-500">{language === 'hi' ? 'Weak Topics' : 'Weak Topics'}</div>
                        </div>
                        <div className="bg-white p-3 rounded-lg text-center col-span-2 md:col-span-1">
                          <div className="text-lg font-bold text-green-600 truncate">{doubtAnalysis.most_active_subject || '-'}</div>
                          <div className="text-xs text-gray-500">{language === 'hi' ? 'सबसे ज्यादा' : 'Most Active'}</div>
                        </div>
                      </div>
                      {doubtAnalysis.recommendation && (
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-sm text-yellow-800">💡 {doubtAnalysis.recommendation}</p>
                        </div>
                      )}
                      {doubtAnalysis.weak_topics?.length > 0 && (
                        <div className="mt-3">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">{language === 'hi' ? 'Weak Topics (ज्यादा बार पूछे):' : 'Weak Topics (asked multiple times):'}</h5>
                          <div className="flex flex-wrap gap-2">
                            {doubtAnalysis.weak_topics.map((topic, idx) => (
                              <span key={idx} className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                {topic.subject}: {topic.topic} ({topic.question_count}x)
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Recent Doubts List */}
                  <h4 className="font-semibold text-gray-700 mb-3">{language === 'hi' ? 'हाल के सवाल' : 'Recent Questions'}</h4>
                  {doubtHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <p>{language === 'hi' ? 'अभी तक कोई सवाल नहीं पूछा। AI से पूछो!' : 'No doubts yet. Ask the AI!'}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {doubtHistory.map((doubt, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 rounded-lg border hover:shadow transition">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">{doubt.subject}</span>
                              <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs">{doubt.topic}</span>
                              {doubt.story_mode && <span className="text-xs">🦸 Story</span>}
                              {doubt.has_image && <span className="text-xs">📷</span>}
                            </div>
                            <span className="text-xs text-gray-400">
                              {doubt.created_at ? new Date(doubt.created_at).toLocaleDateString('hi-IN') : ''}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 line-clamp-2">{doubt.question}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {categories.map(cat => (
          <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${activeCategory === cat.id ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            {cat.name} ({cat.count})
          </button>
        ))}
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {filteredFeatures.map(feature => (
          <div 
            key={feature.id} 
            className={`p-4 rounded-xl text-left transition relative ${
              feature.status === 'pending' 
                ? 'bg-gray-50 border-2 border-dashed border-gray-300 opacity-80' 
                : activeFeature === feature.id 
                  ? 'bg-orange-100 border-2 border-orange-500' 
                  : 'bg-white shadow hover:shadow-lg'
            }`}
          >
            {/* Status Badge - Live or Pending */}
            <span className={`absolute -top-2 -left-2 text-xs px-2 py-0.5 rounded-full font-medium shadow ${
              feature.status === 'live' 
                ? 'bg-green-500 text-white' 
                : 'bg-yellow-400 text-yellow-900'
            }`}>
              {feature.status === 'live' ? '✓ Live' : '⏳ Soon'}
            </span>
            
            {/* Story Mode Badge */}
            {feature.hasStoryMode && (
              <span className="absolute -top-2 right-0 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs px-2 py-0.5 rounded-full font-medium shadow">
                🦸 Story
              </span>
            )}
            
            {/* Image/Voice Badges */}
            {(feature.hasImageUpload || feature.hasVoiceInput) && (
              <div className="absolute -top-2 right-0 flex gap-1">
                {feature.hasImageUpload && <span className="bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">📷</span>}
                {feature.hasVoiceInput && <span className="bg-purple-500 text-white text-xs px-1.5 py-0.5 rounded-full">🎤</span>}
              </div>
            )}
            
            {/* NEW Badge for new features */}
            {feature.isNew && (
              <span className="absolute -top-2 right-0 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs px-2 py-0.5 rounded-full font-medium shadow animate-pulse">
                🆕 NEW
              </span>
            )}
            
            <button 
              onClick={() => { 
                if (feature.status === 'pending') {
                  alert(language === 'hi' ? 'यह feature जल्द आ रहा है! 🚀' : 'This feature is coming soon! 🚀');
                  return;
                }
                setActiveFeature(feature.id); 
                setFormData(feature.id === 'homework-hint' ? { story_mode: false, character: 'chacha' } : {}); 
                setResponse(null); 
              }} 
              className={`w-full text-left ${feature.status === 'pending' ? 'cursor-not-allowed' : ''}`}
              disabled={feature.status === 'pending'}
            >
              <span className="text-2xl block mb-2 mt-2">{feature.icon}</span>
              <h3 className={`font-semibold text-sm ${feature.status === 'pending' ? 'text-gray-500' : ''}`}>{feature.name}</h3>
              <p className="text-xs text-gray-500 mt-1">{feature.desc}</p>
            </button>
            
            {feature.status === 'live' && (
              <button onClick={() => setShowDetailPage(feature.id)} className="mt-2 text-xs text-orange-600 hover:text-orange-700 hover:underline flex items-center gap-1" data-testid={`view-detail-${feature.id}`}>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {language === 'hi' ? 'विवरण देखें' : 'View Details'}
              </button>
            )}
          </div>
        ))}
      </div>
      
      {activeFeature && features.find(f => f.id === activeFeature)?.status === 'live' && (
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <span>{features.find(f => f.id === activeFeature)?.icon}</span>
              {features.find(f => f.id === activeFeature)?.name}
              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">Live</span>
            </h3>
            <button onClick={() => setShowDetailPage(activeFeature)} className="text-sm text-orange-600 hover:underline flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {language === 'hi' ? 'पूरी जानकारी' : 'Full Info'}
            </button>
          </div>
          
          {renderForm()}
          
          <button onClick={handleSubmit} disabled={loading} className="mt-4 w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 font-medium" data-testid="ai-generate-btn">
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {language === 'hi' ? 'AI सोच रहा है... (10-30 सेकंड)' : 'AI is thinking... (10-30 seconds)'}
              </span>
            ) : (language === 'hi' ? '🚀 जनरेट करें' : '🚀 Generate')}
          </button>
          
          {response && (
            <div className={`mt-4 p-4 rounded-lg ${response.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold">{response.success ? (language === 'hi' ? 'AI प्रतिक्रिया:' : 'AI Response:') : (language === 'hi' ? 'त्रुटि:' : 'Error:')}</h4>
                {response.story_mode && response.character && (
                  <span className="text-sm bg-orange-100 text-orange-700 px-2 py-1 rounded-full flex items-center gap-1">
                    {response.character.id === 'chacha' ? '👴' : response.character.id === 'sabu' ? '💪' : response.character.id === 'billu' ? '👦' : '👧'}
                    {response.character.name}
                  </span>
                )}
              </div>
              <div className="whitespace-pre-wrap text-sm bg-white p-3 rounded-lg max-h-96 overflow-y-auto">
                {response.success ? (
                  // Special handling for different response types
                  activeFeature === 'gamified-learning' && response.all_badges ? (
                    <div className="space-y-3">
                      {response.action === 'check_badges' && (
                        <>
                          <div className="text-lg font-semibold mb-2">{language === 'hi' ? '🏅 तुम्हारे Badges:' : '🏅 Your Badges:'}</div>
                          {response.all_badges.length > 0 ? (
                            <div className="grid grid-cols-3 gap-2">
                              {response.all_badges.map((badge, idx) => (
                                <div key={idx} className="p-3 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg text-center border border-yellow-200">
                                  <span className="text-2xl block">{badge.icon}</span>
                                  <span className="text-sm font-medium">{badge.name}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500">{language === 'hi' ? 'अभी कोई badge नहीं है। पढ़ाई करो!' : 'No badges yet. Start learning!'}</p>
                          )}
                          {response.new_badges?.length > 0 && (
                            <div className="mt-3 p-3 bg-green-50 rounded-lg">
                              <span className="text-green-700 font-semibold">{language === 'hi' ? '🎉 नया Badge मिला!' : '🎉 New Badge Earned!'}</span>
                            </div>
                          )}
                        </>
                      )}
                      {response.action === 'get_streak' && (
                        <div className="text-center py-4">
                          <span className="text-5xl block mb-2">🔥</span>
                          <span className="text-3xl font-bold text-orange-600">{response.current_streak} {language === 'hi' ? 'दिन' : 'days'}</span>
                          <p className="text-gray-600 mt-1">{response.streak_message}</p>
                          <p className="text-sm text-gray-500 mt-2">{language === 'hi' ? 'सबसे लंबी streak:' : 'Longest streak:'} {response.longest_streak} {language === 'hi' ? 'दिन' : 'days'}</p>
                        </div>
                      )}
                      {response.action === 'get_leaderboard' && (
                        <>
                          <div className="text-lg font-semibold mb-2">{language === 'hi' ? '🏆 Leaderboard' : '🏆 Leaderboard'}</div>
                          <div className="space-y-2">
                            {response.leaderboard?.map((entry, idx) => (
                              <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${idx < 3 ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200' : 'bg-gray-50'}`}>
                                <div className="flex items-center gap-3">
                                  <span className="text-xl">{entry.badge}</span>
                                  <span className="font-medium">{entry.name}</span>
                                </div>
                                <span className="font-bold text-orange-600">{entry.score} pts</span>
                              </div>
                            ))}
                          </div>
                          {response.my_rank && <p className="mt-3 text-center text-sm text-gray-600">{language === 'hi' ? 'तुम्हारा rank:' : 'Your rank:'} #{response.my_rank}</p>}
                        </>
                      )}
                    </div>
                  ) : activeFeature === 'at-risk-alerts' && response.at_risk_students ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="p-3 bg-red-50 rounded-lg text-center">
                          <span className="text-2xl font-bold text-red-600">{response.total_at_risk}</span>
                          <p className="text-sm text-gray-600">{language === 'hi' ? 'At-Risk' : 'At-Risk'}</p>
                        </div>
                        <div className="p-3 bg-orange-50 rounded-lg text-center">
                          <span className="text-2xl font-bold text-orange-600">{response.high_priority_count}</span>
                          <p className="text-sm text-gray-600">{language === 'hi' ? 'High Priority' : 'High Priority'}</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg text-center">
                          <span className="text-sm text-gray-500">Thresholds: {response.thresholds?.attendance}% / {response.thresholds?.marks}%</span>
                        </div>
                      </div>
                      {response.at_risk_students?.slice(0, 5).map((student, idx) => (
                        <div key={idx} className={`p-3 rounded-lg border ${student.priority === 'high' ? 'bg-red-50 border-red-200' : student.priority === 'medium' ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50'}`}>
                          <div className="flex justify-between">
                            <span className="font-medium">{student.name}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${student.priority === 'high' ? 'bg-red-200 text-red-700' : student.priority === 'medium' ? 'bg-yellow-200 text-yellow-700' : 'bg-gray-200'}`}>{student.priority}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{student.risk_factors?.join(', ')}</p>
                        </div>
                      ))}
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <h4 className="font-medium text-blue-800 mb-2">{language === 'hi' ? '💡 AI सुझाव:' : '💡 AI Recommendations:'}</h4>
                        <p className="text-sm">{response.ai_recommendations}</p>
                      </div>
                    </div>
                  ) : activeFeature === 'attendance-alerts' && response.stats ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-4 gap-2 mb-4">
                        <div className="p-3 bg-green-50 rounded-lg text-center">
                          <span className="text-xl font-bold text-green-600">{response.stats.present}</span>
                          <p className="text-xs">{language === 'hi' ? 'उपस्थित' : 'Present'}</p>
                        </div>
                        <div className="p-3 bg-red-50 rounded-lg text-center">
                          <span className="text-xl font-bold text-red-600">{response.stats.absent}</span>
                          <p className="text-xs">{language === 'hi' ? 'अनुपस्थित' : 'Absent'}</p>
                        </div>
                        <div className="p-3 bg-yellow-50 rounded-lg text-center">
                          <span className="text-xl font-bold text-yellow-600">{response.stats.late}</span>
                          <p className="text-xs">{language === 'hi' ? 'देर से' : 'Late'}</p>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg text-center">
                          <span className="text-xl font-bold text-blue-600">{response.stats.percentage}%</span>
                          <p className="text-xs">{language === 'hi' ? 'उपस्थिति' : 'Attendance'}</p>
                        </div>
                      </div>
                      {response.alerts?.length > 0 && (
                        <div className="space-y-2">
                          {response.alerts.map((alert, idx) => (
                            <div key={idx} className={`p-3 rounded-lg ${alert.type === 'critical' ? 'bg-red-100 border border-red-300' : alert.type === 'warning' ? 'bg-yellow-100 border border-yellow-300' : 'bg-blue-50'}`}>
                              <p className="font-medium">{alert.message}</p>
                              <p className="text-xs text-gray-600">{alert.action}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm">{response.ai_advice}</p>
                      </div>
                    </div>
                  ) : activeFeature === 'weekly-challenge' ? (
                    <div className="space-y-4">
                      {/* Current Challenge View */}
                      {response.has_challenge !== undefined && (
                        response.has_challenge ? (
                          <div className="space-y-4">
                            <div className="p-4 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl">
                              <div className="flex items-center gap-3 mb-3">
                                <span className="text-4xl">🎯</span>
                                <div>
                                  <h3 className="font-bold text-purple-800">{language === 'hi' ? 'इस हफ्ते का Challenge' : 'This Week\'s Challenge'}</h3>
                                  <p className="text-sm text-purple-600">{response.challenge?.subject} - {response.challenge?.topic}</p>
                                </div>
                              </div>
                              <div className="flex gap-4 text-sm">
                                <span className="bg-white px-3 py-1 rounded-full">📝 {response.challenge?.num_questions} {language === 'hi' ? 'प्रश्न' : 'Questions'}</span>
                                <span className="bg-white px-3 py-1 rounded-full">📅 Week: {response.challenge?.week_id}</span>
                              </div>
                            </div>
                            {response.already_submitted ? (
                              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                <p className="font-semibold text-green-700">✅ {language === 'hi' ? 'तुमने यह challenge complete कर लिया!' : 'You have completed this challenge!'}</p>
                                <p className="text-sm text-green-600 mt-1">{language === 'hi' ? 'तुम्हारा Score:' : 'Your Score:'} {response.my_score} | Rank: #{response.my_rank}</p>
                              </div>
                            ) : (
                              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                <p className="font-semibold text-yellow-700">⏳ {language === 'hi' ? 'अभी attempt करो!' : 'Attempt now!'}</p>
                                <p className="text-sm text-yellow-600">{language === 'hi' ? 'Questions नीचे दिए गए हैं।' : 'Questions are given below.'}</p>
                              </div>
                            )}
                            {response.challenge?.questions && (
                              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                <pre className="text-xs whitespace-pre-wrap max-h-48 overflow-y-auto">{response.challenge.questions}</pre>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-6">
                            <span className="text-5xl block mb-3">🎯</span>
                            <p className="text-gray-600">{response.message}</p>
                            <p className="text-sm text-purple-600 mt-2">{language === 'hi' ? 'Teachers नया challenge generate कर सकते हैं।' : 'Teachers can generate new challenge.'}</p>
                          </div>
                        )
                      )}
                      
                      {/* Leaderboard View */}
                      {response.leaderboard && (
                        <div className="space-y-3">
                          <div className="text-center mb-4">
                            <span className="text-4xl">🏆</span>
                            <h3 className="font-bold text-xl">{language === 'hi' ? 'Challenge Leaderboard' : 'Challenge Leaderboard'}</h3>
                            {response.challenge?.subject && (
                              <p className="text-sm text-gray-500">{response.challenge.subject} - {response.challenge.topic}</p>
                            )}
                          </div>
                          <div className="space-y-2">
                            {response.leaderboard.map((entry, idx) => (
                              <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${entry.is_me ? 'bg-purple-100 border-2 border-purple-400' : idx < 3 ? 'bg-gradient-to-r from-yellow-50 to-amber-50' : 'bg-gray-50'}`}>
                                <div className="flex items-center gap-3">
                                  <span className="text-xl">{entry.badge}</span>
                                  <span className="font-medium">{entry.student_name} {entry.is_me && '(You)'}</span>
                                </div>
                                <span className="font-bold text-purple-600">{entry.score} pts</span>
                              </div>
                            ))}
                          </div>
                          {response.participated && (
                            <div className="mt-4 p-3 bg-purple-50 rounded-lg text-center">
                              <p className="text-sm">{language === 'hi' ? 'तुम्हारा Score:' : 'Your Score:'} <span className="font-bold">{response.my_score}</span> | Rank: <span className="font-bold">#{response.my_rank}</span></p>
                            </div>
                          )}
                          <p className="text-xs text-gray-500 text-center">{language === 'hi' ? 'कुल प्रतिभागी:' : 'Total Participants:'} {response.total_participants}</p>
                        </div>
                      )}
                      
                      {/* History View */}
                      {response.history && (
                        <div className="space-y-3">
                          <div className="text-center mb-4">
                            <span className="text-4xl">📊</span>
                            <h3 className="font-bold text-xl">{language === 'hi' ? 'मेरा Challenge Record' : 'My Challenge History'}</h3>
                          </div>
                          <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="p-3 bg-purple-50 rounded-lg text-center">
                              <span className="text-2xl font-bold text-purple-600">{response.stats?.total_challenges}</span>
                              <p className="text-xs">{language === 'hi' ? 'कुल' : 'Total'}</p>
                            </div>
                            <div className="p-3 bg-green-50 rounded-lg text-center">
                              <span className="text-2xl font-bold text-green-600">{response.stats?.average_score}</span>
                              <p className="text-xs">{language === 'hi' ? 'औसत Score' : 'Avg Score'}</p>
                            </div>
                            <div className="p-3 bg-yellow-50 rounded-lg text-center">
                              <span className="text-2xl font-bold text-yellow-600">{response.stats?.best_score}</span>
                              <p className="text-xs">{language === 'hi' ? 'Best Score' : 'Best'}</p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {response.history.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div>
                                  <span className="text-sm font-medium">{item.subject} - {item.topic}</span>
                                  <p className="text-xs text-gray-500">{item.week_id}</p>
                                </div>
                                <div className="text-right">
                                  <span className="font-bold text-purple-600">{item.score}</span>
                                  <p className="text-xs text-gray-500">Rank #{item.rank}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* If nothing specific, show message */}
                      {!response.has_challenge && !response.leaderboard && !response.history && response.message && (
                        <p className="text-center text-gray-600">{response.message}</p>
                      )}
                    </div>
                  ) : (
                    // Default text display for other features
                    typeof (response[getResponseKey()] || response.data?.[getResponseKey()]) === 'object'
                      ? JSON.stringify(response[getResponseKey()] || response.data?.[getResponseKey()] || response.data || response, null, 2)
                      : (response[getResponseKey()] || response.data?.[getResponseKey()] || JSON.stringify(response.data || response, null, 2))
                  )
                ) : (response.message || response.detail || 'Unknown error')}
              </div>
            </div>
          )}
        </div>
      )}

      {/* AI Feature Detail Page Modal */}
      {showDetailPage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <AIFeatureDetailPage 
                featureId={showDetailPage} 
                onBack={() => { setShowDetailPage(null); setActiveFeature(showDetailPage); }} 
                token={token} 
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Main App Component

export default AIFeaturesPage;
